import Box from '@mui/material/Box';
import Slide from '@mui/material/Slide';

import { StickyActionButtons } from '@/components/molecules';
import { useStickyActionBar } from '@/hooks/useStickyActionBar';

/**
 * What the bar occupies at the bottom of a phone viewport: two 44px touch
 * targets plus the row's padding and the border. `SiteLayout` reserves this
 * much after the footer so the bar never covers the last strip of the page,
 * which is why it is exported rather than written twice.
 */
export const STICKY_ACTION_BAR_HEIGHT = 72;

/**
 * §60 L — phones only: WhatsApp beside the free-advice CTA, pinned to the
 * bottom. From `md` up the floating WhatsApp button takes over (§60 K), so
 * there is one WhatsApp affordance at every width and never two.
 *
 * This file is the surface: where it sits, what it is painted with, and when
 * it slides. `useStickyActionBar` decides what it offers and whether it shows,
 * and `StickyActionButtons` draws the row.
 */
export function StickyActionBar() {
  const { visible, whatsappMessage, consultation } = useStickyActionBar();

  return (
    <Slide direction="up" in={visible} appear={false}>
      <Box
        sx={{
          display: { xs: 'block', md: 'none' },
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: (t) => t.zIndex.appBar,
          // Frosted glass, the same treatment as the header: the page shows
          // through blurred, so the bar reads as chrome over the content
          // rather than a slab cutting the bottom off it. The channel variable
          // is what makes one value correct in both schemes.
          bgcolor: 'rgba(var(--mui-palette-background-paperChannel) / 0.92)',
          backdropFilter: 'saturate(180%) blur(12px)',
          borderTop: '1px solid',
          borderColor: 'divider',
          // Clear of the home indicator on iPhones.
          pb: 'env(safe-area-inset-bottom)',
        }}
      >
        <StickyActionButtons whatsappMessage={whatsappMessage} consultation={consultation} />
      </Box>
    </Slide>
  );
}
